/**
 * Modal-dialog behaviour as a Svelte action: focus in, focus trapped, Escape
 * closes, focus restored to whatever opened it.
 *
 * The cart drawer and the €10 survey modal were plain divs. Visually they are
 * modals - they cover the page and take clicks - but a keyboard or screen
 * reader user got none of that: Tab walked straight out of the drawer into the
 * page behind it, Escape did nothing, and nothing announced that a dialog had
 * opened. The only way out was finding the close button by eye.
 *
 * `role="dialog"`, `aria-modal` and the label stay in the MARKUP rather than
 * being set from here: they belong with the element, they are greppable, and
 * svelte-check/eslint can see them. This action owns only the behaviour.
 *
 * Usage:
 *   <div role="dialog" aria-modal="true" aria-label="Shopping cart"
 *        tabindex="-1" use:dialog={{ onClose: () => open = false }}>
 *
 * @param {HTMLElement} node
 * @param {{ onClose?: () => void }} [options]
 */
export function dialog(node, options = {}) {
  let onClose = options.onClose;

  // Whatever had focus when the dialog opened - usually the button that opened
  // it. Focus goes back there on close, or the user is dumped at the top of
  // the document with no idea where they were.
  const opener = document.activeElement;

  const FOCUSABLE = [
    "a[href]",
    "button:not([disabled])",
    'input:not([disabled]):not([type="hidden"])',
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");

  /**
   * Focusable children, in DOM order, skipping anything hidden.
   *
   * Deliberately NOT measured with offsetWidth/offsetHeight: those are always
   * 0 without layout (jsdom, and anything server-rendered), which would empty
   * the list and defeat the trap. `checkVisibility` answers the real question
   * where it exists; otherwise every match counts, which is right for both of
   * our dialogs - neither hides a focusable child.
   */
  function focusable() {
    return [...node.querySelectorAll(FOCUSABLE)].filter((el) =>
      typeof el.checkVisibility === "function" ? el.checkVisibility() : true,
    );
  }

  function focusFirst() {
    const target = focusable()[0] || node;
    target.focus({ preventScroll: true });
  }

  function handleKeydown(event) {
    if (event.key === "Escape") {
      // stopPropagation so one Escape closes one dialog, not every listener
      // up the tree.
      event.stopPropagation();
      onClose?.();
      return;
    }
    if (event.key !== "Tab") return;

    const items = focusable();
    if (items.length === 0) {
      // Nothing to move to - keep focus on the dialog rather than letting it
      // escape to the page behind.
      event.preventDefault();
      node.focus({ preventScroll: true });
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === node)) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  node.addEventListener("keydown", handleKeydown);
  focusFirst();

  return {
    update(next = {}) {
      onClose = next.onClose;
    },
    destroy() {
      node.removeEventListener("keydown", handleKeydown);
      // Only take focus back if it is still inside the dialog being removed -
      // if something else has claimed it since (a checkout redirect, another
      // dialog), stealing it would be worse than leaving it.
      const active = document.activeElement;
      const focusWasInside =
        active === node || node.contains(active) || active === document.body;
      if (
        focusWasInside &&
        opener instanceof HTMLElement &&
        document.contains(opener)
      ) {
        opener.focus({ preventScroll: true });
      }
    },
  };
}
